import { ChangeDetectionStrategy, Component, input, InputSignal, output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { BasicFolder } from '../../models';

export type Breadcrumb = {
    title: string | undefined;
    url?: string;
    id?: number | undefined;
    queryParams?: Record<string, unknown>;
    isCurrent?: boolean;
};

/**
 * Builds a list of breadcrumbs from a folder hierarchy.
 * @param currentFolder The current folder being viewed or containing the asset.
 * @param basePath The base route URL (e.g. '/plays', '/template-criteria').
 * @param isEntityView When true, all folders including currentFolder are links to go back to folder list view.
 */
export function buildFolderBreadcrumbs(
    currentFolder: BasicFolder | null | undefined,
    basePath: string,
    isEntityView = false
): Breadcrumb[] {
    if (!currentFolder || (!currentFolder.id && !currentFolder.name)) {
        return [
            {
                title: 'Root',
                url: isEntityView ? basePath : undefined,
                id: 0,
                queryParams: { folderId: 0 },
                isCurrent: !isEntityView,
            },
        ];
    }

    const folderChain: BasicFolder[] = [];
    let curr: BasicFolder | undefined = currentFolder;
    let hasMissingParent = false;

    while (curr) {
        folderChain.unshift(curr);
        if (curr.parentFolder) {
            curr = curr.parentFolder;
        } else if (curr.parentFolderId && curr.parentFolderId !== 0) {
            hasMissingParent = true;
            break;
        } else {
            break;
        }
    }

    const crumbs: Breadcrumb[] = [
        {
            title: 'Root',
            url: basePath,
            id: 0,
            queryParams: { folderId: 0 },
            isCurrent: false,
        },
    ];

    if (hasMissingParent) {
        crumbs.push({
            title: '...',
            url: undefined,
            isCurrent: false,
        });
    }

    folderChain.forEach((folder, index) => {
        const isLast = index === folderChain.length - 1;
        if (isLast && !isEntityView) {
            crumbs.push({
                title: folder.name,
                id: folder.id,
                isCurrent: true,
            });
        } else {
            crumbs.push({
                title: folder.name,
                url: basePath,
                id: folder.id,
                queryParams: { folderId: folder.id },
                isCurrent: false,
            });
        }
    });

    return crumbs;
}

/**
 * @description ARIA Breadcrumb pattern compliant breadcrumbs
 * {@link https://www.w3.org/WAI/ARIA/apg/patterns/breadcrumb/examples/breadcrumb/}
 */
@Component({
    selector: 'r1-compass-lib-breadcrumb-list',
    imports: [RouterModule, MatButtonModule],
    template: `
        <nav aria-label="Breadcrumb" class="breadcrumb">
            <ol>
                @for (crumb of breadcrumbList(); track $index; let isLast = $last) {
                    <li>
                        @if (crumb.url && !crumb.isCurrent) {
                            <a
                                [routerLink]="[crumb.url]"
                                [queryParams]="
                                    crumb.queryParams ?? (crumb.id !== undefined ? { folderId: crumb.id } : undefined)
                                "
                                [attr.aria-current]="crumb.isCurrent || isLast ? 'page' : null">
                                {{ crumb.title }}
                            </a>
                        } @else {
                            <span [attr.aria-current]="crumb.isCurrent || isLast ? 'page' : null">
                                {{ crumb.title }}
                            </span>
                        }
                    </li>
                } @empty {
                    <button mat-raised-button color="primary" (click)="noBreadcrumbsBackupEvent.emit()" type="button">
                        Go to Root
                    </button>
                }
            </ol>
        </nav>
    `,
    styleUrls: ['./breadcrumb-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreadcrumbListComponent {
    breadcrumbList: InputSignal<Breadcrumb[]> = input.required();
    noBreadcrumbsBackupEvent = output<void>();
}
